import { useCallback, useEffect, useMemo, useRef } from "react";

/** How long the pointer must rest before a hover counts. */
export const HOVER_INTENT_MS = 300;
/** Grace after leaving before a settled hover clears; stops row-to-row flicker. */
export const HOVER_LEAVE_GRACE_MS = 120;

export interface HoverIntent<T> {
  /** Pointer is over `value`; it settles after the dwell unless `leave` comes first. */
  enter: (value: T) => void;
  /** Pointer left whatever it was over. */
  leave: () => void;
  /** Drop everything now, including a settled value (used when a drag starts). */
  cancel: () => void;
}

/**
 * Hover with intent: a value is reported only after the pointer has rested on
 * it for `delay`, and cleared a short grace after it leaves.
 *
 * The listing uses one instance for both the map and the table, so sweeping
 * from one pane to the other cancels whatever was pending in the first.
 * `onSettle` is read through a ref, so the returned handlers are stable and
 * safe to hand to many rows.
 */
const defaultKeyOf = (value: unknown): string => JSON.stringify(value);

export function useHoverIntent<T>(
  onSettle: (value: T | null) => void,
  delay: number = HOVER_INTENT_MS,
  leaveGrace: number = HOVER_LEAVE_GRACE_MS,
  keyOf: (value: T) => string = defaultKeyOf
): HoverIntent<T> {
  const onSettleRef = useRef(onSettle);
  onSettleRef.current = onSettle;
  const enterTimer = useRef<number | null>(null);
  const leaveTimer = useRef<number | null>(null);
  const pendingKey = useRef<string | null>(null);
  const settled = useRef<T | null>(null);

  const clearTimers = useCallback(() => {
    if (enterTimer.current !== null) {
      window.clearTimeout(enterTimer.current);
      enterTimer.current = null;
    }
    if (leaveTimer.current !== null) {
      window.clearTimeout(leaveTimer.current);
      leaveTimer.current = null;
    }
    pendingKey.current = null;
  }, []);

  const enter = useCallback(
    (value: T) => {
      const key = keyOf(value);
      // A mousemove stream over one polygon must not restart the dwell on
      // every pixel, or it would never settle.
      if (pendingKey.current === key) return;
      if (settled.current !== null && keyOf(settled.current) === key) {
        // Already highlighted: just make sure a pending clear is dropped.
        if (leaveTimer.current !== null) {
          window.clearTimeout(leaveTimer.current);
          leaveTimer.current = null;
        }
        return;
      }
      clearTimers();
      pendingKey.current = key;
      enterTimer.current = window.setTimeout(() => {
        enterTimer.current = null;
        pendingKey.current = null;
        settled.current = value;
        onSettleRef.current(value);
      }, delay);
    },
    [clearTimers, delay, keyOf]
  );

  const leave = useCallback(() => {
    clearTimers();
    if (settled.current === null) return;
    leaveTimer.current = window.setTimeout(() => {
      leaveTimer.current = null;
      settled.current = null;
      onSettleRef.current(null);
    }, leaveGrace);
  }, [clearTimers, leaveGrace]);

  const cancel = useCallback(() => {
    clearTimers();
    if (settled.current !== null) {
      settled.current = null;
      onSettleRef.current(null);
    }
  }, [clearTimers]);

  useEffect(() => () => clearTimers(), [clearTimers]);

  // One stable handle: callers put it in effect deps.
  return useMemo(() => ({ enter, leave, cancel }), [enter, leave, cancel]);
}
